import type { ScenarioCharacterCue, ScenarioLine, ScenarioNode } from './types';

const FALLBACK_POSITION_ORDER = ['center', 'left', 'right'] as const;

export function resolveScenarioLineCharacters(
  line: ScenarioLine,
  node: ScenarioNode
): readonly ScenarioCharacterCue[] {
  if (line.characters) {
    return ensureSpeakerVisible(line, line.characters);
  }

  const nodeCharacters = node.characters ?? [];
  if (!line.speakerId) {
    return nodeCharacters;
  }

  if (nodeCharacters.length <= 1) {
    return hasVisibleSpeaker(line.speakerId, nodeCharacters)
      ? nodeCharacters
      : [createFallbackSpeakerCue(line.speakerId, nodeCharacters)];
  }

  const speakerCue = nodeCharacters.find(
    (cue) => cue.id === line.speakerId && cue.visible !== false
  );
  return speakerCue ? [speakerCue] : [createFallbackSpeakerCue(line.speakerId, nodeCharacters)];
}

function ensureSpeakerVisible(
  line: ScenarioLine,
  characters: readonly ScenarioCharacterCue[]
): readonly ScenarioCharacterCue[] {
  if (!line.speakerId || hasVisibleSpeaker(line.speakerId, characters)) {
    return characters;
  }

  const speakerCue = createFallbackSpeakerCue(line.speakerId, characters);
  if (characters.filter((cue) => cue.visible !== false).length >= FALLBACK_POSITION_ORDER.length) {
    return [speakerCue];
  }

  return [speakerCue, ...characters];
}

function hasVisibleSpeaker(
  speakerId: string,
  characters: readonly ScenarioCharacterCue[]
): boolean {
  return characters.some((cue) => cue.id === speakerId && cue.visible !== false);
}

function createFallbackSpeakerCue(
  speakerId: string,
  characters: readonly ScenarioCharacterCue[]
): ScenarioCharacterCue {
  const occupiedPositions = new Set(
    characters.filter((cue) => cue.visible !== false).map((cue) => cue.position)
  );
  const position =
    FALLBACK_POSITION_ORDER.find((candidate) => !occupiedPositions.has(candidate)) ?? 'center';

  return {
    id: speakerId,
    position
  };
}
